"use client";

/**
 * BlockContentMini
 *
 * Tiny, read-only renderer for blocks — meant for card-style previews
 * (hover preview card, gallery card content preview, etc.) where we
 * want the *shape* of the block (heading vs paragraph vs checklist),
 * not the full editor.
 *
 * Why a separate component?
 *   - Content blocks store their children in two places: inline marks
 *     under `value.content` (for paragraph/heading/codeBlock/image), and
 *     sibling block records linked through `block.blockIds` for every
 *     container type (taskList, taskItem, bulletList, listItem, callout,
 *     blockquote, columnLayout, table, …).  Anything that "just walks
 *     value.content" silently produces empty list/task items.
 *   - The fetch strategy differs per call site: gallery cards already
 *     have everything in the global block context; hover previews load
 *     a shallow subtree from IDB + API.  We don't want to bake either
 *     assumption into the renderer, so it takes a `lookup` function.
 *
 * Caller contract:
 *
 *   const { lookup, loading } = useShallowSubtree(rootIds, 4);
 *   <BlockContentMini block={someBlock} lookup={lookup} />
 *
 * For container blocks, the renderer recurses through `block.blockIds`
 * by calling `lookup(id)` — return `undefined` for ids you haven't
 * loaded yet and the renderer will fall back to extracted text.
 */

import React, { useEffect, useState } from "react";
import { FileText, Database } from "lucide-react";
import { renderIcon } from "./editor/renderIcon";
import { appDB } from "@/indexedDB";
import { postWithAuth } from "@/lib/api-helpers";
import type { Block, IPage } from "@/types/block";
import type { ViewCollection } from "@/types/board";
import type { BlockRecord } from "@/indexedDB/db/schema";

// ─── Public types ──────────────────────────────────────────────────────────

export type BlockLookup = (id: string) => Block | BlockRecord | undefined;

export interface BlockContentMiniProps {
  block: Block | BlockRecord;
  /** Resolve a child block id → block.  Return undefined if not loaded. */
  lookup: BlockLookup;
  /** Internal — current recursion depth.  Callers leave this at 0. */
  depth?: number;
  /** Cap recursion to keep previews bounded. */
  maxDepth?: number;
}

// ─── Constants ─────────────────────────────────────────────────────────────

/**
 * Content node types whose CHILDREN live as sibling blocks (linked through
 * `block.blockIds`) rather than inline under `value.content`.  Mirrors the
 * `CONTAINER_TYPES` set in `template/todo-list.ts` — keep them in sync.
 */
const CONTAINER_NODE_TYPES = new Set([
  "bulletList",
  "bullet_list",
  "orderedList",
  "ordered_list",
  "listItem",
  "list_item",
  "blockquote",
  "taskList",
  "task_list",
  "taskItem",
  "task_item",
  "columnLayout",
  "column_layout",
  "columnItem",
  "column_item",
  "table",
  "tableRow",
  "table_row",
  "tableCell",
  "table_cell",
  "tableHeader",
  "table_header",
  "callout",
  "toggle",
]);

// ─── Main renderer ─────────────────────────────────────────────────────────

export const BlockContentMini: React.FC<BlockContentMiniProps> = ({
  block,
  lookup,
  depth = 0,
  maxDepth = 4,
}) => {
  // Dispatch on the top-level blockType first.  Page and collection_view
  // children of a page get a one-line chip; everything else goes through
  // the content-node renderer.
  if (block.blockType === "page") {
    const v = block.value as IPage;
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full text-[11px] text-gray-700 dark:text-gray-200">
        <span
          className="inline-flex items-center justify-center shrink-0"
          style={{ width: 13, height: 13 }}
        >
          {v.icon ? (
            renderIcon(v.icon, { style: { width: 13, height: 13, fontSize: 12 } })
          ) : (
            <FileText size={11} className="text-muted-foreground" />
          )}
        </span>
        <span className="truncate">{v.title || "New page"}</span>
      </span>
    );
  }

  if (block.blockType === "collection_view") {
    const v = block.value as ViewCollection;
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full text-[11px] text-gray-700 dark:text-gray-200">
        <span
          className="inline-flex items-center justify-center shrink-0"
          style={{ width: 13, height: 13 }}
        >
          {v.icon ? (
            renderIcon(v.icon, { style: { width: 13, height: 13, fontSize: 12 } })
          ) : (
            <Database size={11} className="text-muted-foreground" />
          )}
        </span>
        <span className="truncate">{v.title || "New Board"}</span>
      </span>
    );
  }
  
  if (isLikelyEmpty(block)) return null;

  return (
    <ContentNode
      value={(block as any).value}
      blockIds={block.blockIds ?? []}
      lookup={lookup}
      depth={depth}
      maxDepth={maxDepth}
    />
  );
};

/**
 * Shallow "is this block worth rendering?" check.  Returns true when the
 * block has no inline text AND no structural value (image, hr) AND no
 * children to recurse into.  Used by callers to filter empty paragraphs/
 * headings out of preview bodies (see PagePreview.topChildren).
 */
export function isLikelyEmpty(block: Block | BlockRecord): boolean {
  if (block.blockType === "page" || block.blockType === "collection_view") {
    return false;
  }
  const value = (block as any).value;
  if (!value) return true;
  const type = value.type;
  // Structural nodes carry meaning even without text.
  if (
    type === "image" ||
    type === "horizontalRule" ||
    type === "horizontal_rule"
  ) {
    return false;
  }
  // Has child blocks (lists, callouts, blockquotes, …)?  Defer the
  // emptiness decision to recursion.
  if ((block.blockIds ?? []).length > 0) return false;
  // Otherwise treat as empty iff there's no extractable inline text.
  return extractContentText(value).length === 0;
}

// ─── Content-node renderer ─────────────────────────────────────────────────

const ContentNode: React.FC<{
  value: any;
  blockIds: string[];
  lookup: BlockLookup;
  depth: number;
  maxDepth: number;
}> = ({ value, blockIds, lookup, depth, maxDepth }) => {
  if (!value || typeof value !== "object") return null;

  const type: string = value.type ?? "";
  const attrs = value.attrs ?? {};
  const isContainer = CONTAINER_NODE_TYPES.has(type);

  // Container nodes — recurse through their sibling-block children.
  // For non-containers, render their inline content directly.
  const children = isContainer ? blockIds : null;
  const inline = !isContainer ? renderInline(value.content) : null;

  const placeholder = (
    <span className="block h-2 w-full bg-gray-200 dark:bg-gray-700 rounded" />
  );

  /** Render the recursed children of a container, capped by depth. */
  const renderChildren = (max = children?.length ?? 0): React.ReactNode => {
    if (!children || depth >= maxDepth) {
      const text = extractContentText(value);
      return text ? <span>{text}</span> : null;
    }
    const items = children
      .slice(0, max)
      .map((id) => lookup(id))
      .filter((b): b is Block | BlockRecord => Boolean(b))
      .filter((b) => !isLikelyEmpty(b)); // drop empty paragraphs etc.
    if (items.length === 0) {
      const text = extractContentText(value);
      return text ? <span>{text}</span> : null;
    }
    return items.map((c) => (
      <BlockContentMini
        key={c._id}
        block={c}
        lookup={lookup}
        depth={depth + 1}
        maxDepth={maxDepth}
      />
    ));
  };

  /**
   * Render an "item body" for list/task items, where the typical structure
   * is `listItem → paragraph → text`.  Inlining the first paragraph keeps
   * the bullet/checkbox on the same line as the text (otherwise the
   * paragraph renders as its own <p> block and ends up under the marker).
   * Any subsequent siblings (nested lists, callouts, etc.) render as
   * block-level children below.
   */
  const renderItemBody = (): React.ReactNode => {
    if (!children || depth >= maxDepth) {
      const text = extractContentText(value);
      return text || null;
    }
    const kids = children
      .map((id) => lookup(id))
      .filter((b): b is Block | BlockRecord => Boolean(b))
      .filter((b) => !isLikelyEmpty(b));
    if (kids.length === 0) {
      const text = extractContentText(value);
      return text || null;
    }
    const first = kids[0]!;
    const rest = kids.slice(1);
    const firstType = (first as any)?.value?.type;
    const firstIsParagraph =
      firstType === "paragraph" || firstType === "heading";
    return (
      <>
        {firstIsParagraph
          ? renderInline((first as any).value?.content)
          : (
            <BlockContentMini
              block={first}
              lookup={lookup}
              depth={depth + 1}
              maxDepth={maxDepth}
            />
          )}
        {rest.map((c) => (
          <BlockContentMini
            key={c._id}
            block={c}
            lookup={lookup}
            depth={depth + 1}
            maxDepth={maxDepth}
          />
        ))}
      </>
    );
  };

  // Common body styles — use the same theme tokens the gallery card preview
  // uses (var(--c-texPri) / opacity-80) so dark mode and brand-color changes
  // propagate without per-component overrides.
  const TEXT = "text-[11px] leading-snug text-[var(--c-texPri)] opacity-80";

  switch (type) {
    // ─ Leaf nodes (inline content under value.content) ─────────────────────
    case "heading": {
      if (!inline) return null;
      const level = Math.max(1, Math.min(6, Number(attrs.level) || 1));
      const sizeClass =
        level === 1
          ? "text-[15px] font-bold"
          : level === 2
            ? "text-[13px] font-bold"
            : level === 3
              ? "text-[12px] font-semibold"
              : "text-[11px] font-semibold";
      return (
        <div
          role="heading"
          aria-level={level}
          className={`${sizeClass} text-[var(--c-texPri)] opacity-80 truncate m-0`}
        >
          {inline}
        </div>
      );
    }

    case "paragraph":
      if (!inline) return null;
      return (
        <div className={`${TEXT} whitespace-break-spaces break-words`}>
          {inline}
        </div>
      );

    case "codeBlock":
    case "code_block": {
      const codeText = extractContentText(value);
      if (!codeText && !inline) return null;
      return (
        <div className="font-mono text-[10px] leading-snug p-2 rounded overflow-hidden whitespace-pre-wrap break-words bg-muted dark:bg-[var(--modal-bg)] text-muted-foreground dark:text-[#e2e8f0] border border-component">
          {inline || codeText}
        </div>
      );
    }

    case "horizontalRule":
    case "horizontal_rule":
      return <hr className="border-[var(--ca-borSecTra)] my-1 m-0" />;

    case "image": {
      const src = attrs.src;
      if (!src) return placeholder;
      return (
        <div className="h-10 w-full overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={attrs.alt ?? ""}
            className="w-full h-full object-cover"
          />
        </div>
      );
    }

    // ─ Container nodes (children via blockIds, recurse) ────────────────────
    case "bulletList":
    case "bullet_list":
      return (
        <ul className={`list-disc list-inside ${TEXT} m-0 pl-1 space-y-0.5`}>
          {renderChildren()}
        </ul>
      );

    case "orderedList":
    case "ordered_list":
      return (
        <ol className={`list-decimal list-inside ${TEXT} m-0 pl-1 space-y-0.5`}>
          {renderChildren()}
        </ol>
      );

    case "taskList":
    case "task_list":
      return <div className="space-y-0.5">{renderChildren()}</div>;

    case "listItem":
    case "list_item": {
      // Inline the first paragraph next to the bullet/number; render any
      // remaining children (e.g. a nested list) as a block below.
      const body = renderItemBody();
      if (!body) return null;
      return <li className={TEXT}>{body}</li>;
    }

    case "taskItem":
    case "task_item": {
      const checked = Boolean(attrs.checked);
      const body = renderItemBody();
      if (!body) return null;
      return (
        <label
          className={`flex items-start gap-1.5 ${TEXT} min-w-0 m-0`}
        >
          <input
            type="checkbox"
            checked={checked}
            readOnly
            tabIndex={-1}
            className="mt-[3px] shrink-0 h-[10px] w-[10px] accent-emerald-600 pointer-events-none"
          />
          <span
            className={`flex-1 min-w-0 ${
              checked ? "line-through opacity-60" : ""
            }`}
          >
            {body}
          </span>
        </label>
      );
    }

    case "blockquote":
      return (
        <div
          role="blockquote"
          className="border-l-4 border-[var(--ca-borSecTra)] pl-2 italic text-[var(--c-texPri)] opacity-70 text-[11px] leading-snug"
        >
          {renderChildren()}
        </div>
      );

    case "callout": {
      const icon: string | undefined = attrs.icon;
      const bg: string | undefined = attrs.backgroundColor;
      return (
        <div
          className={`flex items-start gap-1.5 ${TEXT} px-1.5 py-1 rounded min-w-0`}
          style={{ backgroundColor: bg || "rgba(251, 243, 219, 0.5)" }}
        >
          {icon && (
            <span
              className="shrink-0 inline-flex items-center justify-center"
              style={{ width: 12, height: 12, fontSize: 11 }}
            >
              {renderIcon(icon, {
                style: { width: 12, height: 12, fontSize: 11 },
              })}
            </span>
          )}
          <div className="flex-1 min-w-0">{renderChildren()}</div>
        </div>
      );
    }

    case "toggle":
      return (
        <div className={`flex items-start gap-1 ${TEXT} min-w-0`}>
          <span className="shrink-0 opacity-60" style={{ fontSize: 9, lineHeight: "14px" }}>
            ▸
          </span>
          <div className="flex-1 min-w-0">{renderChildren()}</div>
        </div>
      );

    // ─ Complex containers — render their children stacked, no chrome ─────
    case "table":
    case "tableRow":
    case "table_row":
    case "tableCell":
    case "table_cell":
    case "tableHeader":
    case "table_header":
    case "columnLayout":
    case "column_layout":
    case "columnItem":
    case "column_item":
      return <div className={TEXT}>{renderChildren()}</div>;

    // ─ Unknown node — fall back to extracted text ─────────────────────────
    default: {
      const text = extractContentText(value);
      if (!text) return placeholder;
      return <div className={`${TEXT} truncate`}>{text}</div>;
    }
  }
};

// ─── Inline rendering (text nodes + marks) ─────────────────────────────────

/**
 * Render an array of ProseMirror inline nodes (text/marks/hardBreak) into
 * a React fragment.  Supported marks: bold, italic, underline, strike,
 * code, link.  Unknown marks are dropped silently.
 */
export function renderInline(content: any): React.ReactNode {
  if (!Array.isArray(content) || content.length === 0) return null;
  return content.map((node, idx) => {
    if (!node || typeof node !== "object") return null;
    if (node.type === "text" && typeof node.text === "string") {
      let el: React.ReactNode = node.text;
      const marks: Array<{ type: string }> = Array.isArray(node.marks)
        ? node.marks
        : [];
      for (const mark of marks) {
        switch (mark.type) {
          case "bold":
            el = <strong key={`b${idx}`}>{el}</strong>;
            break;
          case "italic":
            el = <em key={`i${idx}`}>{el}</em>;
            break;
          case "underline":
            el = <u key={`u${idx}`}>{el}</u>;
            break;
          case "strike":
            el = <s key={`s${idx}`}>{el}</s>;
            break;
          case "code":
            el = (
              <code
                key={`c${idx}`}
                className="font-mono bg-gray-100 dark:bg-gray-800 px-0.5 rounded"
              >
                {el}
              </code>
            );
            break;
          case "link":
            el = (
              <span key={`l${idx}`} className="underline">
                {el}
              </span>
            );
            break;
        }
      }
      return <React.Fragment key={idx}>{el}</React.Fragment>;
    }
    if (node.type === "hardBreak" || node.type === "hard_break") {
      return <br key={idx} />;
    }
    if (Array.isArray(node.content)) {
      return (
        <React.Fragment key={idx}>{renderInline(node.content)}</React.Fragment>
      );
    }
    return null;
  });
}

/**
 * Walk a value subtree and collect plain text — used as a fallback when
 * we can't render a node properly (unknown type, container with no loaded
 * children, etc.).
 */
export function extractContentText(value: any): string {
  const acc: string[] = [];
  const visit = (n: any) => {
    if (!n) return;
    if (typeof n === "string") {
      acc.push(n);
      return;
    }
    if (typeof n.text === "string") acc.push(n.text);
    if (Array.isArray(n)) n.forEach(visit);
    else if (Array.isArray(n.content)) n.content.forEach(visit);
  };
  visit(value);
  return acc.join(" ").trim();
}

// ─── Shallow-subtree loader ────────────────────────────────────────────────

/**
 * Load a shallow tree of blocks (root ids + their descendants up to
 * `maxDepth` levels) into local state and return a synchronous `lookup`
 * function over the result.
 *
 * Each level: bulkGet from IDB, then a single `/api/blocks/get-many`
 * batch for whatever's still missing.  Containers (taskList, bulletList,
 * callout, …) drive descent — leaf nodes don't trigger another round.
 */
export function useShallowSubtree(
  rootIds: string[],
  maxDepth = 3,
): { loading: boolean; lookup: BlockLookup } {
  const [blocks, setBlocks] = useState<Map<string, BlockRecord>>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(false);

  // Stable join key so repeated identical id arrays don't re-trigger fetches.
  const key = rootIds.join(",");

  useEffect(() => {
    let cancelled = false;
    if (rootIds.length === 0) {
      setBlocks(new Map());
      return;
    }
    setLoading(true);

    (async () => {
      const collected = new Map<string, BlockRecord>();
      let pending = [...rootIds];
      let level = 0;

      while (pending.length > 0 && level <= maxDepth) {
        // Skip ids we've already collected (cycles or duplicates).
        const wanted = pending.filter((id) => !collected.has(id));
        if (wanted.length === 0) break;

        // IDB first, API for the rest.
        const fromIdb = (await appDB.blocks.bulkGet(wanted)).filter(
          (r): r is BlockRecord => Boolean(r),
        );
        fromIdb.forEach((b) => collected.set(b._id, b));

        const stillMissing = wanted.filter((id) => !collected.has(id));
        if (stillMissing.length > 0) {
          try {
            const res = (await postWithAuth("/api/blocks/get-many", {
              blockIds: stillMissing,
            })) as { success?: boolean; blocks?: Block[] };
            if (res?.success && Array.isArray(res.blocks)) {
              res.blocks.forEach((b) =>
                collected.set(b._id, b as BlockRecord),
              );
            }
          } catch {
            // Continue with whatever we have — preview just degrades.
          }
        }

        if (cancelled) return;
        // Publish progress so the body re-renders as deeper levels land.
        setBlocks(new Map(collected));

        // Build the next level: child ids of any container blocks we just
        // resolved.  Non-containers don't have sibling-block children.
        const next: string[] = [];
        for (const id of wanted) {
          const b = collected.get(id);
          if (!b) continue;
          const type = (b.value as any)?.type ?? "";
          const isContainer = CONTAINER_NODE_TYPES.has(type);
          // Also descend into page/collection_view blockIds — these can
          // appear as nested page children in a preview body.
          if (isContainer || b.blockType !== "content") {
            (b.blockIds ?? []).forEach((cid) => next.push(cid));
          }
        }
        pending = next;
        level += 1;
      }

      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, maxDepth]);

  const lookup: BlockLookup = (id) => blocks.get(id);
  return { loading, lookup };
}

export default BlockContentMini;
